import React from 'react';
import { toast, type Id, type ToastOptions } from 'react-toastify';
import { useIntl, type MessageDescriptor } from 'react-intl';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert, { type AlertColor } from '@mui/material/Alert';
import { styled } from '@mui/material/styles';

import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

const ToastButton = styled(Button)(({ theme }) => ({ color: theme.palette.common.white }));

type ToastChild = React.ReactNode | MessageDescriptor;

// An action the caller adds next to Close. A descriptor rather than a node, so the toast keeps
// ownership of how its buttons look and the caller only supplies label and intent.
export type ToastAction = { label: MessageDescriptor; onClick: () => void };

type ToastProps = {
  children: ToastChild;
  type: AlertColor;
  action?: ToastAction;
  closeToast?: () => void; // injected by react-toastify
};

const isMessageDescriptor = (child: ToastChild): child is MessageDescriptor =>
  typeof child === 'object' && child !== null && 'id' in child && 'defaultMessage' in child;

export const Toast = ({ children, type, action, closeToast }: ToastProps) => {
  const { formatMessage } = useIntl();

  const content = children && isMessageDescriptor(children) ? formatMessage(children) : children;

  return (
    <Alert
      variant="filled"
      severity={type}
      data-element={`toast.${type}`}
      iconMapping={{
        success: <CheckCircleOutlineOutlinedIcon fontSize="inherit" />,
      }}
      action={
        // Stacked, not inline: the toast container is a fixed 380px, so two buttons side by side
        // squeeze the message. `stretch` keeps the labels aligned without depending on text direction.
        <Stack direction="column" alignItems="stretch">
          {action && (
            <ToastButton variant="text" size="small" onClick={action.onClick}>
              {formatMessage(action.label)}
            </ToastButton>
          )}
          <ToastButton variant="text" size="small" onClick={closeToast}>
            {formatMessage({ id: 'button.close', defaultMessage: 'Close' })}
          </ToastButton>
        </Stack>
      }
    >
      {content}
    </Alert>
  );
};

type RenderToastOptions = ToastOptions & { action?: ToastAction };

const renderToast =
  (type: AlertColor) =>
  (child: ToastChild, { action, ...options }: RenderToastOptions = {}): Id =>
    toast.success(
      <Toast type={type} action={action}>
        {child}
      </Toast>,
      options,
    );

export default {
  success: renderToast('success'),
  info: renderToast('info'),
  warn: renderToast('warning'),
  error: renderToast('error'),
  // Exposed so callers that raise a toast with a `toastId` can withdraw it again without reaching past
  // this module into react-toastify.
  dismiss: toast.dismiss,
};
